
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle, Lock, ArrowRight } from 'lucide-react';
import { fetchSurveyById, fetchSurveyQuestions, fetchKioskQuestions, verifyKioskCode } from '../services/surveyService';
import { Survey, SurveyQuestionDraft } from '../types';
import SurveyResponseView from './SurveyResponseView';
import SurveyResponseKioskView from './SurveyResponseKioskView';

const SurveyLoader: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [questions, setQuestions] = useState<SurveyQuestionDraft[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Kiosk access gate. The verified code is held so the runner can submit through
  // the code-checked RPC (kiosk reads/writes are blocked at the DB layer).
  const [gate, setGate] = useState<'checking' | 'locked' | 'open'>('checking');
  const [accessCode, setAccessCode] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const [gateErr, setGateErr] = useState<string | null>(null);
  const [unlocking, setUnlocking] = useState(false);

  useEffect(() => {
    if (id) loadData(id);
  }, [id]);

  // Load the survey first. Non-kiosk surveys are public, so we can also load their
  // questions immediately. Kiosk questions are gated and loaded only after unlock.
  const loadData = async (surveyId: string) => {
    setLoading(true);
    try {
      const surveyData = await fetchSurveyById(surveyId);
      if (!surveyData) throw new Error('Survey not found');
      setSurvey(surveyData);
      if (!surveyData.is_kiosk) {
        const qs = await fetchSurveyQuestions(surveyId);
        setQuestions(qs);
        setGate('open');
      }
    } catch (e) {
      console.error(e);
      setError('Failed to load survey. It may have been deleted or is inaccessible.');
    } finally {
      setLoading(false);
    }
  };

  // Pull the gated kiosk questions once a valid code is known, then open the runner.
  const openKiosk = async (code: string) => {
    if (!survey) return;
    try {
      const qs = await fetchKioskQuestions(survey.id, code);
      setQuestions(qs);
      setAccessCode(code);
      if (code) localStorage.setItem(`kiosk_${survey.id}`, code);
      setGate('open');
    } catch (e) {
      console.error(e);
      setGate('locked');
      setGateErr('Could not load this kiosk.');
    }
  };

  // For kiosk surveys: try the URL/remembered code automatically; lock otherwise.
  useEffect(() => {
    if (!survey || !survey.is_kiosk) return;
    (async () => {
      const stored = localStorage.getItem(`kiosk_${survey.id}`) || '';
      const code = searchParams.get('code') || stored;
      try {
        const res = await verifyKioskCode(survey.id, code);
        if (!res.required) {
          await openKiosk('');
        } else if (res.ok) {
          await openKiosk(code);
        } else {
          setGate('locked');
        }
      } catch {
        setGate('locked');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [survey, searchParams]);

  const tryUnlock = async () => {
    if (!survey || !codeInput.trim()) return;
    setUnlocking(true);
    setGateErr(null);
    try {
      const res = await verifyKioskCode(survey.id, codeInput.trim());
      if (res.ok) {
        await openKiosk(codeInput.trim());
      } else {
        setGateErr('Incorrect access code.');
      }
    } catch {
      setGateErr('Could not verify the code.');
    } finally {
      setUnlocking(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-900 gap-4">
        <Loader2 className="animate-spin text-indigo-500" size={48} />
        <p className="text-slate-400 animate-pulse">Loading Survey...</p>
      </div>
    );
  }

  if (error || !survey) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-900 p-6 text-center">
        <div className="bg-slate-800 p-8 rounded-2xl border border-slate-700 max-w-md w-full">
          <AlertCircle size={48} className="mx-auto text-red-400 mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Unavailable</h2>
          <p className="text-slate-400 mb-6">{error || 'Survey not found.'}</p>
          <button onClick={() => navigate('/')} className="bg-slate-700 hover:bg-slate-600 text-white px-6 py-2 rounded-lg font-medium transition-colors">
            Return Home
          </button>
        </div>
      </div>
    );
  }

  // Kiosk access gate
  if (survey.is_kiosk && gate !== 'open') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-900 p-6">
        <div className="bg-slate-800 p-8 rounded-2xl border border-slate-700 max-w-sm w-full text-center">
          <div className="h-14 w-14 rounded-2xl bg-slate-900 border border-slate-700 flex items-center justify-center mx-auto mb-4">
            <Lock size={26} className="text-indigo-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-1">Kiosk locked</h2>
          <p className="text-slate-400 text-sm mb-6">Enter the access code for this kiosk device.</p>

          {gate === 'checking' ? (
            <Loader2 className="animate-spin text-indigo-500 mx-auto" size={28} />
          ) : (
            <div className="space-y-3">
              <input
                type="password"
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && tryUnlock()}
                placeholder="Access code"
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-3 text-center text-white tracking-widest focus:outline-none focus:border-indigo-500"
                autoFocus
              />
              {gateErr && <p className="text-red-400 text-xs">{gateErr}</p>}
              <button
                onClick={tryUnlock}
                disabled={unlocking || !codeInput.trim()}
                className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 text-white font-bold py-3 rounded-lg transition-colors"
              >
                {unlocking ? <Loader2 className="animate-spin" size={18} /> : <ArrowRight size={18} />} Unlock kiosk
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Guard against an empty survey so the runner never crashes on questions[0].
  if (questions.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-900 p-6 text-center">
        <div className="bg-slate-800 p-8 rounded-2xl border border-slate-700 max-w-md w-full">
          <AlertCircle size={48} className="mx-auto text-amber-400 mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Not ready yet</h2>
          <p className="text-slate-400 mb-6">This survey has no questions yet. Please check back later.</p>
          <button onClick={() => navigate('/')} className="bg-slate-700 hover:bg-slate-600 text-white px-6 py-2 rounded-lg font-medium transition-colors">
            Return Home
          </button>
        </div>
      </div>
    );
  }

  if (survey.is_kiosk) {
    return (
      <SurveyResponseKioskView
        survey={survey}
        questions={questions}
        accessCode={accessCode}
        onReset={() => loadData(survey.id)}
      />
    );
  }

  return <SurveyResponseView survey={survey} questions={questions} />;
};

export default SurveyLoader;
